import { useRef, useEffect } from "react";
import { ChartLine } from "@phosphor-icons/react";
import { Card } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../contexts/CurrencyContext";
import { IRevenueVsSpendEntity } from "../interface";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
  Chart,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import i18next from "i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

interface RevenueAndSpendChartProps {
  data?: IRevenueVsSpendEntity;
  showWithTax: boolean;
}

const RevenueAndSpendChart = ({
  data,
  showWithTax,
}: RevenueAndSpendChartProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();

  const chartRef = useRef<Chart<"line"> | null>(null);
  const chartWithTaxRef = useRef<Chart<"line"> | null>(null);

  const chartData: ChartData<"line"> = {
    labels: data?.labels || [],
    datasets: [
      {
        label: t("dashboard.revenueVsSpend.revenue"),
        data: data?.estimateRevenue || [],
        borderColor: "#22c55e",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("dashboard.summary.iapRevenue"),
        data: data?.iapRevenue || [],
        borderColor: "#f59e0b",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("dashboard.revenueVsSpend.spend"),
        data: data?.totalCost_without_tax || [],
        borderColor: "#ef4444",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("dashboard.revenueVsSpend.net"),
        data: data?.totalNet_without_tax || [],
        borderColor: "#91bbff",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartDataWithTax: ChartData<"line"> = {
    labels: data?.labels || [],
    datasets: [
      {
        label: t("dashboard.revenueVsSpend.revenueWithTax"),
        data: data?.estimateRevenue || [],
        borderColor: "#22c55e",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("dashboard.summary.iapRevenue"),
        data: data?.iapRevenue || [],
        borderColor: "#f59e0b",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("dashboard.revenueVsSpend.spendWithTax"),
        data: data?.totalCost_with_tax || [],
        borderColor: "#ef4444",
        fill: true,
        tension: 0.4,
      },
      {
        label: t("dashboard.revenueVsSpend.netWithTax"),
        data: data?.totalNet_with_tax || [],
        borderColor: "#91bbff",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
            weight: "bold",
          },
          color: "var(--text-color)",
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 12,
          padding: 16,
        },
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        displayColors: false,
        usePointStyle: true,
        callbacks: {
          title: function (items) {
            return `${t("charts.axes.date")}: ${items[0].label}`;
          },
          label: function (context) {
            return [
              `${context.dataset.label} : ${formatCurrency(
                context.raw as number
              )}`,
            ];
          },
          labelTextColor: function (context) {
            return (context.dataset.borderColor as string) || "#222";
          },
        },
        backgroundColor: "#fff",
        titleColor: "#222",
        bodyColor: "#222",
        borderColor: "#e5e7eb",
        borderWidth: 2,
        padding: 12,
        boxPadding: 6,
        titleMarginBottom: 10,
        bodySpacing: 10,
        titleFont: {
          size: 15,
          weight: "bolder",
        },
        bodyFont: {
          size: 14,
          weight: "normal",
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "x",
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          // drag: {
          //   enabled: false,
          // },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: "var(--font-inter)",
            size: 12,
          },
          color: "#6b7280",
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: "var(--font-inter)",
            size: 12,
          },
          color: "#6b7280",
          callback: function (tickValue: number | string) {
            if (typeof tickValue === "number") {
              return formatCurrency(tickValue);
            }
            return tickValue;
          },
        },
      },
    },
  };

  useEffect(() => {
    const setGradients = (
      chartInstance: Chart<"line"> | null,
      stops: [string, string][]
    ) => {
      if (!chartInstance) return;
      const { ctx, chartArea } = chartInstance;
      if (!chartArea) return;

      const height = chartArea.bottom;
      stops.forEach(([start, end], index) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, start);
        gradient.addColorStop(1, end);
        chartInstance.data.datasets[index].backgroundColor = gradient;
      });

      chartInstance.update();
    };

    setTimeout(() => {
      setGradients(chartRef.current, [
        ["rgba(34, 197, 94, 0.4)", "rgba(34, 197, 94, 0)"],
        ["rgba(239, 68, 68, 0.2)", "rgba(239, 68, 68, 0)"],
        ["rgba(145, 187, 255, 0.4)", "rgba(145, 187, 255, 0)"],
      ]);
    }, 100);

    if (showWithTax) {
      setTimeout(() => {
        setGradients(chartWithTaxRef.current, [
          ["rgba(34, 197, 94, 0.4)", "rgba(34, 197, 94, 0)"],
          ["rgba(239, 68, 68, 0.2)", "rgba(239, 68, 68, 0)"],
          ["rgba(145, 187, 255, 0.4)", "rgba(145, 187, 255, 0)"],
        ]);
      }, 100);
    }
  }, [data, showWithTax, i18next.language]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 pl-2">
        <div className="flex items-center gap-2">
          <ChartLine size={20} className="text-gray-700" weight="duotone" />
          <h1 className="text-md font-bold text-gray-700">
            {t("dashboard.revenueVsSpend.title")}
          </h1>
        </div>
      </div>
      <div
        className={`flex flex-col md:flex-row overflow-hidden ${
          showWithTax ? "gap-4" : ""
        }`}
      >
        <Card
          theme={{
            root: {
              base: "flex rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800",
            },
          }}
          className={`transition-all duration-500 ${
            showWithTax ? "w-full md:w-1/2" : "w-full"
          }`}
        >
          <h1 className="text-md font-bold text-gray-700">
            {t("dashboard.revenueVsSpend.withoutTax")}
          </h1>
          <div className="h-80">
            <Line ref={chartRef} data={chartData} options={options} />
          </div>
        </Card>
        {showWithTax && (
          <div
            className={`transition-all duration-500 ease-in-out w-full md:w-1/2 ${
              showWithTax
                ? "opacity-100 transform translate-x-0"
                : "opacity-0 transform translate-x-full pointer-events-none w-0 md:w-0 m-1 p-0"
            }`}
          >
            <Card
              theme={{
                root: {
                  base: "flex rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800",
                },
              }}
            >
              <h1 className="text-md font-bold text-gray-700">
                {t("common.withTax")}
              </h1>
              <div className="h-80">
                <Line
                  ref={chartWithTaxRef}
                  data={chartDataWithTax}
                  options={options}
                />
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueAndSpendChart;
